import React, {useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Typography} from '@mui/material'
import {format, parseISO} from 'date-fns'
import StorefrontIcon from '@mui/icons-material/Storefront'

import PageTitle from '../../components/PageTitle'
import {usePaceMerchantList} from './usePaceMerchantList'
import {
  PageWrapper,
  SectionCard,
  SectionHeader,
  SectionHeaderLeft,
  SectionTitle,
  SectionBadge,
  OrdersFilterBar,
  SearchInput,
  StyledTable,
  StyledTh,
  StyledTr,
  StyledTd,
  TypeBadge,
  PaginationRow,
  PaginationInfo,
  PaginationBtns,
  LoadingOverlay,
  EmptyState,
  EmptyText,
} from '../pace_analytics/styled'
import {IconButton} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

const PAGE_SIZE = 15

// PACE has one allocation type. Older records may still say PERFORMANCE / COST /
// BALANCED / WEIGHTED — all of them are PACE, shown as a single chip.
const PACE_TYPES = new Set(['PACE', 'PERFORMANCE', 'COST', 'BALANCED', 'WEIGHTED'])
const ALLOCATION_TYPE_COLORS = {
  PACE: {bg: '#e3f2fd', text: '#1565c0'},
}

function fmtDatetime(val) {
  if (!val) return null
  try {
    return format(parseISO(val), 'dd MMM yyyy, HH:mm')
  } catch (_) {
    return val
  }
}

function Dash() {
  return <Typography sx={{fontSize: '0.78rem', color: '#bdbdbd'}}>—</Typography>
}

export default function PaceMerchantList() {
  const {isLoading, brands, totalBrands, loadBrands} = usePaceMerchantList()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  useEffect(() => {
    loadBrands()
  }, [loadBrands])

  const filtered = useMemo(() => {
    if (!search.trim()) return brands
    const q = search.toLowerCase()
    return brands.filter(b =>
      b.merchantName?.toLowerCase().includes(q) || b.merchantOid?.toLowerCase().includes(q)
    )
  }, [brands, search])

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE)
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  const handleSearch = (e) => {
    setSearch(e.target.value)
    setPage(0)
  }

  return (
    <PageWrapper>
      <PageTitle
        title='PACE · Onboarded Merchants'
        subtitle='Every brand allowed the PACE entry in its allocation rules, and when it was enabled'
      />

      <SectionCard>
        <SectionHeader>
          <SectionHeaderLeft>
            <Box sx={{
              width: 32, height: 32, borderRadius: 2,
              backgroundColor: '#e3f2fd', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: '#1565c0',
            }}>
              <StorefrontIcon sx={{fontSize: 16}} />
            </Box>
            <Box>
              <SectionTitle>PACE-Enabled Merchants</SectionTitle>
              <Typography sx={{fontSize: '0.68rem', color: '#9e9e9e', mt: 0.25}}>
                Brands using PACE for courier allocation
              </Typography>
            </Box>
          </SectionHeaderLeft>
          <SectionBadge>{totalBrands} Merchants</SectionBadge>
        </SectionHeader>

        <OrdersFilterBar>
          <SearchInput
            placeholder='Search by merchant name…'
            value={search}
            onChange={handleSearch}
          />
        </OrdersFilterBar>

        {isLoading ? (
          <LoadingOverlay><CircularProgress size={28} /></LoadingOverlay>
        ) : paged.length === 0 ? (
          <EmptyState><EmptyText>No merchants found</EmptyText></EmptyState>
        ) : (
          <Box sx={{overflowX: 'auto'}}>
            <StyledTable>
              <thead>
                <tr>
                  <StyledTh>#</StyledTh>
                  <StyledTh>Merchant</StyledTh>
                  <StyledTh>Merchant ID</StyledTh>
                  <StyledTh>Allocation Types</StyledTh>
                  <StyledTh>Enabled At</StyledTh>
                  <StyledTh>Enabled By</StyledTh>
                  <StyledTh>Disabled At</StyledTh>
                  <StyledTh>Disabled By</StyledTh>
                </tr>
              </thead>
              <tbody>
                {paged.map((brand, idx) => {
                  const globalIdx = page * PAGE_SIZE + idx + 1
                  const isLast = idx === paged.length - 1
                  const enabledAt = fmtDatetime(brand.paceEnabledAt)
                  const disabledAt = fmtDatetime(brand.paceDisabledAt)
                  return (
                    <StyledTr key={brand.merchantOid || idx} isLast={isLast}>
                      <StyledTd>
                        <Typography sx={{fontSize: '0.75rem', color: '#bdbdbd'}}>{globalIdx}</Typography>
                      </StyledTd>
                      <StyledTd bold>
                        {brand.merchantName ? (
                          <Typography sx={{fontSize: '0.82rem', fontWeight: 700, color: '#424242'}}>
                            {brand.merchantName}
                          </Typography>
                        ) : (
                          <Box>
                            <Typography sx={{fontSize: '0.75rem', color: '#bdbdbd', fontStyle: 'italic'}}>
                              Name unavailable
                            </Typography>
                            <Typography sx={{fontSize: '0.65rem', fontFamily: 'monospace', color: '#9e9e9e', mt: 0.25}}>
                              {brand.merchantOid}
                            </Typography>
                          </Box>
                        )}
                      </StyledTd>
                      <StyledTd>
                        <Typography sx={{fontSize: '0.75rem', fontFamily: 'monospace', color: '#757575'}}>
                          {brand.merchantId ?? '—'}
                        </Typography>
                      </StyledTd>
                      <StyledTd>
                        <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap'}}>
                          {brand.allocationTypes && brand.allocationTypes.length > 0
                            ? [...new Set(brand.allocationTypes.map((t) => (PACE_TYPES.has(String(t || '').toUpperCase()) ? 'PACE' : t)))].map(type => {
                                const c = ALLOCATION_TYPE_COLORS[type] || {bg: '#f5f5f5', text: '#757575'}
                                return (
                                  <TypeBadge key={type} bg={c.bg} textColor={c.text}>
                                    {type}
                                  </TypeBadge>
                                )
                              })
                            : <Dash />
                          }
                        </Box>
                      </StyledTd>
                      <StyledTd>
                        {enabledAt ? (
                          <Typography sx={{fontSize: '0.78rem', color: '#424242'}}>{enabledAt}</Typography>
                        ) : <Dash />}
                      </StyledTd>
                      <StyledTd>
                        {brand.paceEnabledBy
                          ? <Typography sx={{fontSize: '0.75rem', color: '#757575'}}>{brand.paceEnabledBy}</Typography>
                          : <Dash />
                        }
                      </StyledTd>
                      <StyledTd>
                        {disabledAt ? (
                          <Typography sx={{fontSize: '0.78rem', color: '#c62828'}}>{disabledAt}</Typography>
                        ) : <Dash />}
                      </StyledTd>
                      <StyledTd>
                        {brand.paceDisabledBy
                          ? <Typography sx={{fontSize: '0.75rem', color: '#757575'}}>{brand.paceDisabledBy}</Typography>
                          : <Dash />
                        }
                      </StyledTd>
                    </StyledTr>
                  )
                })}
              </tbody>
            </StyledTable>
          </Box>
        )}

        {!isLoading && filtered.length > 0 && (
          <PaginationRow>
            <PaginationInfo>
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} results
            </PaginationInfo>
            <PaginationBtns>
              <IconButton size='small' disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <ChevronLeftIcon fontSize='small' />
              </IconButton>
              <IconButton size='small' disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                <ChevronRightIcon fontSize='small' />
              </IconButton>
            </PaginationBtns>
          </PaginationRow>
        )}
      </SectionCard>
    </PageWrapper>
  )
}
